import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useCallback,
  useEffect,
  useRef,
} from "react";

const TradingContext = createContext();
const STORAGE_KEY = "gainwise-trading-state-v2";
const BASE_PORTFOLIO_BALANCE = 100;
const REMOTE_STATE_ENDPOINTS = [
  (base) => `${base}/get-trading-state.php`,
  (base) => `${base}/update-trading-state.php`,
];
const REMOTE_BASE_CANDIDATES = [
  import.meta.env.VITE_BACKEND_API_BASE_URL,
  import.meta.env.VITE_AUTH_API_BASE_URL,
  "/backend",
  "/api",
  "https://gainwisefx.com/api",
].filter(Boolean);

export { TradingContext };

const DEFAULT_MARKETS = [
  { symbol: "BTCUSDT", displaySymbol: "BTC/USDT", name: "Bitcoin", price: 0, change24h: 0, volume24h: 0 },
  { symbol: "ETHUSDT", displaySymbol: "ETH/USDT", name: "Ethereum", price: 0, change24h: 0, volume24h: 0 },
  { symbol: "BNBUSDT", displaySymbol: "BNB/USDT", name: "BNB", price: 0, change24h: 0, volume24h: 0 },
  { symbol: "SOLUSDT", displaySymbol: "SOL/USDT", name: "Solana", price: 0, change24h: 0, volume24h: 0 },
  { symbol: "ADAUSDT", displaySymbol: "ADA/USDT", name: "Cardano", price: 0, change24h: 0, volume24h: 0 },
];

const ACTIONS = {
  SET_SELECTED_PAIR: "SET_SELECTED_PAIR",
  UPSERT_MARKET: "UPSERT_MARKET",
  BULK_MARKETS: "BULK_MARKETS",
  ADD_NOTIFICATION: "ADD_NOTIFICATION",
  REMOVE_NOTIFICATION: "REMOVE_NOTIFICATION",
  ADD_ORDER: "ADD_ORDER",
  UPDATE_ORDER: "UPDATE_ORDER",
  ADD_TRANSACTION: "ADD_TRANSACTION",
  UPDATE_TRANSACTION: "UPDATE_TRANSACTION",
  REMOVE_TRANSACTION: "REMOVE_TRANSACTION",
  CLEAR_TRANSACTIONS: "CLEAR_TRANSACTIONS",
  APPLY_COMPLETED_TRADE: "APPLY_COMPLETED_TRADE",
  SYNC_ACCOUNT_STATE: "SYNC_ACCOUNT_STATE",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
};

const initialState = {
  isLoading: false,
  error: null,
  marketData: DEFAULT_MARKETS,
  selectedPair: DEFAULT_MARKETS[0],
  account: {
    startingBalance: BASE_PORTFOLIO_BALANCE,
    currentBalance: BASE_PORTFOLIO_BALANCE,
    equity: BASE_PORTFOLIO_BALANCE,
    availableBalance: BASE_PORTFOLIO_BALANCE,
    lockedBalance: 0,
    totalPnL: 0,
    realizedPnL: 0,
    totalInvested: 0,
    completedTrades: 0,
    winningTrades: 0,
    losses: 0,
  },
  portfolio: {
    totalValue: BASE_PORTFOLIO_BALANCE,
    totalPnL: 0,
    totalPnLPercent: 0,
    availableBalance: BASE_PORTFOLIO_BALANCE,
    totalInvested: 0,
    completedTrades: 0,
    winningTrades: 0,
  },
  transactions: [],
  orders: [],
  trades: [],
  notifications: [],
};

const loadInitialState = () => {
  if (typeof window === "undefined") {
    return initialState;
  }

  try {
    const savedState = window.localStorage.getItem(STORAGE_KEY);
    if (!savedState) {
      return initialState;
    }

    const parsedState = JSON.parse(savedState);
    return {
      ...initialState,
      ...parsedState,
      account: {
        ...initialState.account,
        ...(parsedState.account || {}),
      },
      portfolio: {
        ...initialState.portfolio,
        ...(parsedState.portfolio || {}),
      },
      marketData: parsedState.marketData || initialState.marketData,
      selectedPair: parsedState.selectedPair || initialState.selectedPair,
      transactions: pruneTransactions(parsedState.transactions || []),
      orders: parsedState.orders || [],
      trades: parsedState.trades || [],
      notifications: parsedState.notifications || [],
    };
  } catch (error) {
    return initialState;
  }
};

const roundTo = (value, precision = 2) => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Number(value.toFixed(precision));
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const pruneTransactions = (transactions) => {
  const cutoff = Date.now() - THIRTY_DAYS_MS;
  return transactions.filter((transaction) => {
    const timestamp = new Date(transaction.timestamp || Date.now()).getTime();
    return Number.isFinite(timestamp) && timestamp >= cutoff;
  });
};

const upsertMarket = (markets, nextMarket) => {
  const index = markets.findIndex((market) => market.symbol === nextMarket.symbol);
  if (index === -1) {
    return [...markets, nextMarket];
  }

  return markets.map((market, marketIndex) =>
    marketIndex === index ? { ...market, ...nextMarket } : market
  );
};

const tradingReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_SELECTED_PAIR:
      return {
        ...state,
        selectedPair: action.payload,
      };

    case ACTIONS.UPSERT_MARKET: {
      const marketData = upsertMarket(state.marketData, action.payload);
      const selectedPair =
        state.selectedPair.symbol === action.payload.symbol
          ? { ...state.selectedPair, ...action.payload }
          : state.selectedPair;

      return {
        ...state,
        marketData,
        selectedPair,
      };
    }

    case ACTIONS.BULK_MARKETS:
      return {
        ...state,
        marketData: action.payload,
        selectedPair:
          action.payload.find((market) => market.symbol === state.selectedPair.symbol) ||
          action.payload[0] ||
          state.selectedPair,
      };

    case ACTIONS.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [action.payload, ...state.notifications].slice(0, 100),
      };

    case ACTIONS.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          (notification) => notification.id !== action.payload
        ),
      };

    case ACTIONS.ADD_ORDER:
      return {
        ...state,
        orders: [action.payload, ...state.orders],
      };

    case ACTIONS.UPDATE_ORDER:
      return {
        ...state,
        orders: state.orders.map((order) =>
          order.id === action.payload.id ? { ...order, ...action.payload } : order
        ),
      };

    case ACTIONS.ADD_TRANSACTION:
      return {
        ...state,
        transactions: pruneTransactions([action.payload, ...state.transactions]),
      };

    case ACTIONS.UPDATE_TRANSACTION:
      return {
        ...state,
        transactions: pruneTransactions(
          state.transactions.some(
            (transaction) => transaction.id === action.payload.id
          )
            ? state.transactions.map((transaction) =>
                transaction.id === action.payload.id
                  ? { ...transaction, ...action.payload }
                  : transaction
              )
            : [action.payload, ...state.transactions]
        ),
      };

    case ACTIONS.REMOVE_TRANSACTION:
      return {
        ...state,
        transactions: pruneTransactions(
          state.transactions.filter(
            (transaction) => transaction.id !== action.payload
          )
        ),
      };

    case ACTIONS.CLEAR_TRANSACTIONS:
      return {
        ...state,
        transactions: [],
      };

    case ACTIONS.APPLY_COMPLETED_TRADE: {
      const { trade, profitIncrease, investedIncrease } = action.payload;
      const nextCompletedTrades = state.portfolio.completedTrades + 1;
      const nextWinningTrades =
        state.portfolio.winningTrades + (profitIncrease > 0 ? 1 : 0);
      const nextPnL = roundTo(state.portfolio.totalPnL + profitIncrease);
      const nextValue = roundTo(state.portfolio.totalValue + profitIncrease);
      const nextAvailable = roundTo(
        state.portfolio.availableBalance + profitIncrease
      );
      const nextInvested = roundTo(
        state.portfolio.totalInvested + investedIncrease
      );
      const nextPnLPercent =
        nextInvested > 0 ? roundTo((nextPnL / nextInvested) * 100) : 0;

      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          totalValue: nextValue,
          totalPnL: nextPnL,
          totalPnLPercent: nextPnLPercent,
          availableBalance: nextAvailable,
          totalInvested: nextInvested,
          completedTrades: nextCompletedTrades,
          winningTrades: nextWinningTrades,
        },
        trades: [trade, ...state.trades],
        transactions: pruneTransactions(
          state.transactions.some((transaction) => transaction.id === trade.id)
            ? state.transactions.map((transaction) =>
                transaction.id === trade.id ? { ...transaction, ...trade } : transaction
              )
            : [trade, ...state.transactions]
        ),
      };
    }

    case ACTIONS.SYNC_ACCOUNT_STATE: {
      const account = {
        ...state.account,
        ...action.payload,
      };
      const totalInvested = roundTo(account.totalInvested);
      const totalPnLPercent =
        totalInvested > 0 ? roundTo((account.totalPnL / totalInvested) * 100) : 0;

      return {
        ...state,
        account,
        portfolio: {
          ...state.portfolio,
          totalValue: roundTo(account.equity),
          totalPnL: roundTo(account.totalPnL),
          totalPnLPercent,
          availableBalance: roundTo(account.availableBalance),
          totalInvested,
          completedTrades: account.completedTrades,
          winningTrades: account.winningTrades,
        },
      };
    }

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

const createNotification = (payload) => ({
  id: crypto.randomUUID(),
  timestamp: new Date().toISOString(),
  ...payload,
});

const getStoredBackendUser = () => {
  try {
    const rawUser = localStorage.getItem("trading_app_user") || localStorage.getItem("userData");
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
};

const getStoredAuthToken = () =>
  localStorage.getItem("authToken") ||
  localStorage.getItem("token") ||
  localStorage.getItem("trading_app_token") ||
  "";

const createAuthHeaders = () => {
  const token = getStoredAuthToken();
  const user = getStoredBackendUser();
  const headers = { "Content-Type": "application/json" };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (user?.id) {
    headers["X-User-Id"] = String(user.id);
  }

  return headers;
};

const fetchRemoteTradingState = async () => {
  for (const base of REMOTE_BASE_CANDIDATES) {
    try {
      const response = await fetch(`${base}/get-trading-state.php`, {
        method: "GET",
        headers: createAuthHeaders(),
      });

      if (!response.ok) {
        continue;
      }

      const payload = await response.json();
      if (payload?.success) {
        return payload;
      }
    } catch {
      continue;
    }
  }

  return null;
};

const persistRemoteTradingState = async (account) => {
  for (const base of REMOTE_BASE_CANDIDATES) {
    try {
      const response = await fetch(`${base}/update-trading-state.php`, {
        method: "POST",
        headers: createAuthHeaders(),
        body: JSON.stringify({ account }),
      });

      if (response.ok) {
        return true;
      }
    } catch {
      continue;
    }
  }

  return false;
};

export const TradingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(tradingReducer, initialState, loadInitialState);
  const hasLoadedRemoteState = useRef(false);
  const lastPersistedSnapshot = useRef("");

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (hasLoadedRemoteState.current) {
      return;
    }

    hasLoadedRemoteState.current = true;

    fetchRemoteTradingState().then((payload) => {
      if (!payload?.account) {
        return;
      }

      dispatch({
        type: ACTIONS.SYNC_ACCOUNT_STATE,
        payload: payload.account,
      });
    });
  }, []);

  useEffect(() => {
    const user = getStoredBackendUser();
    if (!user?.id) {
      return;
    }

    const accountSnapshot = {
      startingBalance: roundTo(state.account.startingBalance),
      currentBalance: roundTo(state.account.currentBalance),
      equity: roundTo(state.account.equity),
      availableBalance: roundTo(state.account.availableBalance),
      lockedBalance: roundTo(state.account.lockedBalance),
      totalPnL: roundTo(state.account.totalPnL),
      realizedPnL: roundTo(state.account.realizedPnL),
      totalInvested: roundTo(state.account.totalInvested),
      completedTrades: state.account.completedTrades,
      winningTrades: state.account.winningTrades,
      losses: state.account.losses,
    };

    const serializedSnapshot = JSON.stringify(accountSnapshot);
    if (lastPersistedSnapshot.current === serializedSnapshot) {
      return;
    }

    lastPersistedSnapshot.current = serializedSnapshot;
    persistRemoteTradingState(accountSnapshot);
  }, [state.account]);

  const setSelectedPair = useCallback((pair) => {
    dispatch({ type: ACTIONS.SET_SELECTED_PAIR, payload: pair });
  }, []);

  const upsertMarketData = useCallback((market) => {
    dispatch({ type: ACTIONS.UPSERT_MARKET, payload: market });
  }, []);

  const setMarketData = useCallback((markets) => {
    dispatch({ type: ACTIONS.BULK_MARKETS, payload: markets });
  }, []);

  const addNotification = useCallback((notification) => {
    dispatch({
      type: ACTIONS.ADD_NOTIFICATION,
      payload: createNotification(notification),
    });
  }, []);

  const removeNotification = useCallback((id) => {
    dispatch({ type: ACTIONS.REMOVE_NOTIFICATION, payload: id });
  }, []);

  const addOrder = useCallback((order) => {
    dispatch({ type: ACTIONS.ADD_ORDER, payload: order });
  }, []);

  const updateOrder = useCallback((id, patch) => {
    dispatch({ type: ACTIONS.UPDATE_ORDER, payload: { id, ...patch } });
  }, []);

  const addTransaction = useCallback((transaction) => {
    dispatch({ type: ACTIONS.ADD_TRANSACTION, payload: transaction });
  }, []);

  const updateTransaction = useCallback((id, patch) => {
    dispatch({ type: ACTIONS.UPDATE_TRANSACTION, payload: { id, ...patch } });
  }, []);

  const clearTransactions = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_TRANSACTIONS });
  }, []);

  const removeTransaction = useCallback((id) => {
    dispatch({ type: ACTIONS.REMOVE_TRANSACTION, payload: id });
  }, []);

  const recordPendingTransaction = useCallback((transaction) => {
    const payload = {
      id: transaction.id || crypto.randomUUID(),
      status: "pending",
      timestamp: transaction.timestamp || new Date().toISOString(),
      ...transaction,
    };

    addTransaction(payload);
    return payload;
  }, [addTransaction]);

  const recordCompletedTrade = useCallback((tradeData) => {
    const trade = {
      id: tradeData.id || crypto.randomUUID(),
      timestamp: tradeData.timestamp || new Date().toISOString(),
      status: "completed",
      ...tradeData,
    };
    const realizedProfit = roundTo(Math.max(0, trade.profit || 0));
    const investedIncrease = roundTo(
      Math.max(0, trade.investedAmount || trade.notional || 0)
    );

    dispatch({
      type: ACTIONS.APPLY_COMPLETED_TRADE,
      payload: {
        trade,
        profitIncrease: realizedProfit,
        investedIncrease,
      },
    });

    dispatch({ type: ACTIONS.ADD_TRANSACTION, payload: trade });
    return trade;
  }, []);

  const syncAccountState = useCallback((snapshot) => {
    dispatch({
      type: ACTIONS.SYNC_ACCOUNT_STATE,
      payload: {
        startingBalance: roundTo(snapshot.startingBalance ?? BASE_PORTFOLIO_BALANCE),
        currentBalance: roundTo(snapshot.currentBalance ?? snapshot.equity ?? 0),
        equity: roundTo(snapshot.equity ?? snapshot.currentBalance ?? 0),
        availableBalance: roundTo(
          snapshot.availableBalance ?? snapshot.currentBalance ?? 0
        ),
        lockedBalance: roundTo(snapshot.lockedBalance ?? snapshot.activeTradingAmount ?? 0),
        totalPnL: roundTo(snapshot.totalProfitLoss ?? snapshot.totalPnL ?? 0),
        realizedPnL: roundTo(snapshot.realizedProfitLoss ?? snapshot.realizedPnL ?? 0),
        totalInvested: roundTo(snapshot.totalInvested ?? snapshot.activeTradingAmount ?? 0),
        completedTrades: snapshot.sessionsCompleted ?? snapshot.completedTrades ?? 0,
        winningTrades: snapshot.wins ?? snapshot.winningTrades ?? 0,
        losses: snapshot.losses ?? 0,
      },
    });
  }, []);

  const setLoading = useCallback((value) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: value });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_ERROR });
  }, []);

  const setError = useCallback((message) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: message });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      setSelectedPair,
      upsertMarketData,
      setMarketData,
      addNotification,
      removeNotification,
      addOrder,
      updateOrder,
      addTransaction,
      updateTransaction,
      removeTransaction,
      clearTransactions,
      recordPendingTransaction,
      recordCompletedTrade,
      syncAccountState,
      setLoading,
      clearError,
      setError,
      updatePortfolio: () => {},
      addTrade: recordCompletedTrade,
      updateTrade: updateTransaction,
      fetchMarketData: async () => state.marketData,
      fetchPortfolio: async () => state.portfolio,
      fetchBalance: async () => state.portfolio.availableBalance,
      fetchTrades: async () => state.trades,
      initializeTradingData: async () => state,
    }),
    [
      state,
      setSelectedPair,
      upsertMarketData,
      setMarketData,
      addNotification,
      removeNotification,
      addOrder,
      updateOrder,
      addTransaction,
      updateTransaction,
      removeTransaction,
      clearTransactions,
      recordPendingTransaction,
      recordCompletedTrade,
      syncAccountState,
      setLoading,
      clearError,
      setError,
    ]
  );

  return (
    <TradingContext.Provider value={value}>{children}</TradingContext.Provider>
  );
};

export const useTrading = () => {
  const context = useContext(TradingContext);

  if (!context) {
    throw new Error("useTrading must be used within a TradingProvider");
  }

  return context;
};

export default TradingContext;
